// Canonical sharing-UI template — authenticated grantee viewer.
// Router-agnostic: the app reads its route param (shareId) and passes it in, along
// with a renderer for the artifact and (optionally) its router Link.

import React, { useEffect, useState } from "react";
import { SharingApiError, type SharingClient, type SharedViewResponse } from "./types";
import { DefaultLink, type ArtifactRenderer, type SharingLinkComponent } from "./common";

export interface SharedViewProps {
  shareId: string;
  client: SharingClient;
  renderArtifact: ArtifactRenderer;
  /** Set false when the app already gates the route behind auth. */
  isAuthenticated?: boolean;
  LinkComponent?: SharingLinkComponent;
  backHref?: string;
}

export const SharedView: React.FC<SharedViewProps> = ({ shareId, client, renderArtifact, isAuthenticated = true, LinkComponent = DefaultLink, backHref = "/delt" }) => {
  const [data, setData] = useState<SharedViewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !shareId) return;
    (async () => {
      try {
        setData(await client.getShared(shareId));
      } catch (err) {
        if (err instanceof SharingApiError && err.status === 410) setGone(true);
        setError(err instanceof Error ? err.message : "Kunne ikke hente delt beregning.");
      }
    })();
  }, [client, isAuthenticated, shareId]);

  return (
    <div className="sm-page">
      <div className="sm-container">
        <div className="sm-breadcrumb">
          <LinkComponent href={backHref}>Delt med meg</LinkComponent>
          <span>/</span>
          <span>{data?.meta.title ?? "Delt beregning"}</span>
        </div>

        {!isAuthenticated && (
          <div className="sm-alert sm-alert-info" style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-start" }}>
            <div><strong>Innlogging kreves.</strong> Denne delingen er knyttet til en konto — logg inn for å åpne den.</div>
            <a href="/logg-inn" className="sm-btn sm-btn-sm sm-btn-primary"><i className="bi bi-box-arrow-in-right"></i> Logg inn</a>
          </div>
        )}

        {isAuthenticated && error && (
          <div className="sm-error-box">
            <strong>{gone ? "Beregningen er fjernet:" : "Feil:"}</strong>
            <pre className="sm-error-details">{error}</pre>
          </div>
        )}

        {isAuthenticated && data && (
          <>
            <div className="sm-page-header">
              <h1><i className="bi bi-share"></i> {data.meta.title}</h1>
              <p>
                Delt av {data.share.ownerName || data.share.ownerEmail} · kun visning
                {data.meta.subtitle && <> · {data.meta.subtitle}</>}
              </p>
            </div>
            <div className="sm-card">
              <div className="sm-card-body">{renderArtifact(data.artifact, data.share.resourceType)}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SharedView;
